/**
 * 视频播放记录条目。
 *
 * <p>用于统一沉淀课程/培训播放过程中的源类型、播放引擎与恢复动作，
 * 方便组件优化后的联调、测试与问题回放。</p>
 */
export type VideoPlaybackRecord = {
    /** 当前播放会话 ID。 */
    sessionId?: string
    /** 业务模块类型。 */
    module: 'course' | 'train'
    /** 课程或培训 ID。 */
    courseId?: string | number
    /** 课时 ID。 */
    lessonId?: string | number
    /** 页面类型。 */
    pageType?: string
    /** 当前阶段。 */
    stage: string
    /** 当前事件分类。 */
    eventType?: 'session' | 'source' | 'state' | 'error' | 'result'
    /** 当前播放源类型。 */
    sourceType?: string
    /** 本次尝试的目标播放源类型。 */
    targetSourceType?: string
    /** 当前播放引擎。 */
    playbackEngine?: string
    /** 本次尝试的目标播放引擎。 */
    targetPlaybackEngine?: string
    /** 当前实际播放地址。 */
    activeSourceUrl?: string
    /** 当前兜底播放地址。 */
    fallbackVideoUrl?: string
    /** 当前清晰度。 */
    selectedQuality?: string
    /** 是否已回退到 mp4。 */
    usingFallbackMp4?: boolean
    /** 当前播放会话内的尝试次数。 */
    attemptCount?: number
    /** 当前播放会话内的回退次数。 */
    fallbackCount?: number
    /** 当前播放会话最终播放源类型。 */
    finalSourceType?: string
    /** 当前播放会话最终结果。 */
    result?: 'success' | 'fallback' | 'failure' | 'pending'
    /** 距离本次播放会话开始的耗时。 */
    elapsedMs?: number
    /** 附加说明。 */
    message?: string
    /** 记录时间戳。 */
    timestamp: number
}

const STORAGE_KEY = 'videoPlaybackReportRecords'
const MAX_RECORDS = 50

/**
 * 读取当前本地播放记录。
 *
 * @returns 播放记录列表
 */
export function getVideoPlaybackReports(): VideoPlaybackRecord[] {
    try {
        const raw = uni.getStorageSync(STORAGE_KEY)
        if (!raw) {
            return []
        }
        const records = JSON.parse(raw)
        return Array.isArray(records) ? records : []
    } catch (error) {
        console.warn('读取视频播放记录失败', error)
        return []
    }
}

/**
 * 保存一条视频播放记录，并保留最近一批数据用于测试回看。
 *
 * @param record 待记录的播放信息
 */
export function appendVideoPlaybackReport(record: Omit<VideoPlaybackRecord, 'timestamp'>) {
    const nextRecord: VideoPlaybackRecord = {
        ...record,
        timestamp: Date.now()
    }
    const records = getVideoPlaybackReports()
    records.unshift(nextRecord)
    uni.setStorageSync(STORAGE_KEY, JSON.stringify(records.slice(0, MAX_RECORDS)))
}

/**
 * 清空本地视频播放记录。
 */
export function clearVideoPlaybackReports() {
    uni.removeStorageSync(STORAGE_KEY)
}
